<script setup lang="ts">
import { api } from '@/api';
import { type EmailSettingsEditDto, EmailSettingsEditDtoTypeEnum } from '@/api/generator';
import Button from '@/components/buttons/Button.vue';
import EmailSettings from '@/components/EmailSettings/EmailSettings.vue';
import VeeSubmitButton from '@/components/forms/SubmitButton/VeeSubmitButton.vue';
import { useVeeForm } from '@/components/forms/useVeeForm';
import { stopErrorPropagation } from '@/errorHandling';
import { error, success as successIcon } from '@/components/icons/icons';
import { useAuthenticationStore } from '@/stores/authenticationStore';
import {
  type CompletedEventArgs,
  type CompletedStep,
  completedStepsOrdered,
} from '@/views/InstallationView/types';
import { omit } from 'lodash';
import { ref } from 'vue';
import InstallationStep from './InstallationStep.vue';

interface EmailSettingsFormProps {
  emailSettings: EmailSettingsEditDto;
  completedStep: CompletedStep;
  settingsAreProven?: boolean;
}

const props = withDefaults(defineProps<EmailSettingsFormProps>(), {
  settingsAreProven: false,
});
const emit = defineEmits<{
  completed: [args: CompletedEventArgs];
}>();

const { form } = useVeeForm<EmailSettingsEditDto>({
  options: {
    initialValues: {
      ...omit(props.emailSettings, ['useHostDefaultEmailSettings', '$type']),
      $type:
        EmailSettingsEditDtoTypeEnum.PtwPtw100AppZeroApplicationSharedConfigurationDtoEmailSettingsEditDtoPtwPtw100AppZeroApplicationShared,
    },
  },
  schemaName: 'EmailSettingsEditDto',
  mode: 'edit',
});

const { handleSubmit: handleSubmitEmailSettings } = form;
const showSuccessMessage = ref(false);
const showErrorMessage = ref(false);

function hideMessages() {
  showSuccessMessage.value = false;
  showErrorMessage.value = false;
}

const onSubmit = handleSubmitEmailSettings(
  async (emailSettingsEditDto) => {
    hideMessages();
    await useAuthenticationStore().checkAuthentication();
    const { success } = await api.installApi.updateEmailSettings({ emailSettingsEditDto }, [
      stopErrorPropagation,
    ]);
    if (success) {
      showSuccessMessage.value = true;
      emit('completed', { shouldUpdate: true, preventNextNavigation: true });
    } else {
      showErrorMessage.value = true;
    }
  },
  {
    onSubmitValidationErrorCb: () => {
      hideMessages();
    },
  },
);
</script>

<template>
  <form @submit="onSubmit">
    <InstallationStep>
      <template #content>
        <div class="w-lg-66">
          <EmailSettings />
        </div>
      </template>
      <template #footer>
        <v-alert v-if="showSuccessMessage" class="mt-8 fit-content" type="success" :icon="false">
          <template #text>
            <v-icon class="mr-2" :icon="successIcon"></v-icon>
            <span class="font-weight-semibold">{{ $t('EmailSettingsValid') }}</span>
          </template>
        </v-alert>
        <v-alert v-if="showErrorMessage" class="mt-8 fit-content" type="error" :icon="false">
          <template #text>
            <v-icon class="mr-2" :icon="error"></v-icon>
            <span class="font-weight-semibold">{{ $t('EmailSettingsErrorMsg') }}</span>
          </template>
        </v-alert>
        <VSpacer />
        <VeeSubmitButton class="mt-8" :text="$t('TestAndSave')" />
        <Button
          variant="secondary"
          class="mt-8 ml-2"
          :disabled="completedStepsOrdered.indexOf(completedStep) <= 1 || !props.settingsAreProven"
          :label="$t('Next')"
          @click="
            emit('completed', {
              shouldUpdate: false,
              preventNextNavigation: false,
            })
          "
        />
      </template>
    </InstallationStep>
  </form>
</template>

<style lang="scss" scoped>
.step-content {
  padding-bottom: var(--v-footer-height);
}
</style>
